import { FunctionComponent, useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuItem, Select, useMediaQuery } from '@mui/material';
import { cryptoCoins } from '@monorepo/client-common';
import { IUnitTestProps, WithdrawPayload } from '@monorepo/client-types';
import { Stack, TextField, useTheme } from '@monorepo/nova-ui';

interface Props extends IUnitTestProps {
  form: UseFormReturn<WithdrawPayload>;
}

const CryptoWithdraw: FunctionComponent<Props> = ({ form, testId }) => {
  const { t } = useTranslation(['withdrawFundsNova']);
  useEffect(() => {
    return () => {
      form.clearErrors([
        'metadata.walletAddress',
        'metadata.cryptoCurrency',
        'amount',
      ]);
    };
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      gap={isMobile ? 2 : 6}
      data-testid={testId}
    >
      <Controller
        rules={{ required: true }}
        name={'metadata.walletAddress'}
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            error={!!fieldState.error}
            value={field.value || ''}
            placeholder={t('withdrawFundsNova:wallet_address_placeholder')}
            fullWidth
            size={'small'}
          />
        )}
      />
      <Controller
        rules={{
          required: true,
          validate: (value) =>
            value !== 'placeholder' ||
            t('withdrawFundsNova:crypto_currency_required'),
        }}
        name={'metadata.cryptoCurrency'}
        control={form.control}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            error={!!fieldState.error}
            value={field.value || 'placeholder'}
            fullWidth
            sx={{ borderRadius: 3 }}
            size={'small'}
          >
            <MenuItem key={100000} disabled value={'placeholder'}>
              {t('withdrawFundsNova:crypto_currency_placeholder')}
            </MenuItem>
            {cryptoCoins.map((curr, index) => (
              <MenuItem key={index} value={curr.value}>
                {curr.text}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </Stack>
  );
};

export default CryptoWithdraw;
